import { useMutation } from 'react-query';
import usePlanStore, { CountryFormState } from 'store/planStore';
import moment from 'moment/moment';
import { updatePlan } from '../../../../../../api/services/mediaPlanning/updatePlan';
import { useError } from '../../../common/Errors/ErrorContext/ErrorContext';

type DdxPayload =
  | { type: 'omitted' }
  | { type: 'fixed'; value?: number; totalDDX?: number }
  | { type: 'percentage'; value?: number; totalDDX?: number };

function isDdxNullsSentinel(ddx: any) {
  return (
    ddx != null && ddx.type == null && ddx.value == null && ddx.totalDDX == null
  );
}

const useUpdatePlan = (id?: string) => {
  const { generalInfoState } = usePlanStore();
  const { showError } = useError();

  const convertDatesToISO = (
    form: Partial<CountryFormState>
  ): Partial<CountryFormState> => {
    const convert = (date?: string | null) =>
      date ? moment(date, 'DD-MM-YYYY').format('YYYY-MM-DD') : date;
    return {
      ...form,
      startDate: convert(form.startDate) || '',
      availableTouchpoints: form.availableTouchpoints,
      youTubeConfig: form.youTubeConfig && {
        ...form.youTubeConfig,
        ...(process.env.REACT_APP_API_VERSION !== 'v3' && {
          skipShare: form.youTubeConfig?.skipValue,
          unskipShare: form.youTubeConfig?.unSkipValue,
        }),
      },
      metaConfig: form.metaConfig
        ? {
            ...form.metaConfig,
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            startDate: convert(form.metaConfig.startDate),
          }
        : undefined,
      offline: {
        ...form.offline,
        tv: form.offline?.tv
          ? {
              ...form.offline?.tv,
              ...(form.offline.tv.startDate && {
                startDate: moment(
                  form.offline.tv.startDate,
                  'DD-MM-YYYY'
                ).format('YYYY-MM-DD'),
              }),
              ...(form.offline.tv.endDate && {
                endDate: moment(form.offline.tv.endDate, 'DD-MM-YYYY').format(
                  'YYYY-MM-DD'
                ),
              }),
            }
          : undefined,
      },
    };
  };

  const toDdxPayload = (ddx: any): DdxPayload | undefined => {
    if (isDdxNullsSentinel(ddx)) return undefined;
    if (!ddx || ddx.type === 'omitted') return { type: 'omitted' };

    return { type: ddx.type, value: ddx.value, totalDDX: ddx.totalDDX };
  };

  const convertCountryFormsToISO = (
    stored: Record<string, Partial<CountryFormState>>
  ): Record<string, Partial<CountryFormState>> =>
    Object.fromEntries(
      Object.entries(stored).map(([countryISO, form]) => {
        const converted = convertDatesToISO(form);
        const { ddx, ...rest } = converted as any;

        const ddxPayload = toDdxPayload(ddx);

        const base: Partial<CountryFormState> = {
          ...rest,
        };

        return [
          countryISO,
          ddxPayload ? { ...base, ddx: ddxPayload as any } : base,
        ];
      })
    );

  const {
    mutate: updateMediaPlan,
    mutateAsync: asyncUpdateMediaPlan,
    data: response,
    isLoading,
    isError,
  } = useMutation(
    async (passedId: string | null) => {
      const updatedStoredCountryForm =
        usePlanStore.getState().storedCountryForms;

      const countrySetupWithISO = convertCountryFormsToISO(
        updatedStoredCountryForm as any
      );

      return updatePlan({
        body: {
          campaignName: generalInfoState.campaignName,
          campaignCode: generalInfoState.campaignCode,
          division: generalInfoState.division,
          brand: generalInfoState.brand,
          category: generalInfoState.category,
          countrySetup: countrySetupWithISO,
        },
        id: id ?? passedId ?? '',
      });
    },
    {
      onError: (error: any) => {
        const errorMessage =
          error?.response?.data?.message || error.message || 'Unknown error';
        showError(errorMessage);
      },
    }
  );

  return {
    updateMediaPlan,
    asyncUpdateMediaPlan,
    data: response?.data,
    isLoading,
    isError,
  };
};

export default useUpdatePlan;
